import { useEffect, useRef, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import {
  EmptyState,
  Link2OffIcon,
  Loader,
  Text,
  WarningIcon,
} from '@buffer-mono/popcorn'

import { AccessDenied } from '~publish/components/AccessDenied'
import { useOrganizationSwitcher } from '~publish/components/MainSidebar/components/OrganizationSwitcher/useOrganizationSwitcher'
import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { classifyPostFetchError } from '~publish/helpers/posts/classifyPostFetchError'
import type { PostFetchFailure } from '~publish/helpers/posts/classifyPostFetchError'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { buildPostPermalinkTarget } from './buildPostPermalinkTarget'

import styles from '~publish/styles/centered-loader.module.css'

/**
 * Narrower than `PostCard_Post` — the destination refetches the post anyway.
 * Sibling ids are the exception: cards skip them (CORE-5605), but group size is
 * the only way to tell a multi-channel post from one alone in its group.
 */
export const GetPostPermalink = graphql(`
  query GetPostPermalink($id: PostId!) {
    post(input: { id: $id }) {
      id
      status
      postGroup {
        id
        posts {
          id
        }
      }
      channel {
        id
        organizationId
        allowedActions
        name
        displayName
        avatar
        service
      }
    }
  }
`)

/**
 * The shareable form of a post. The details dialog is only ever mounted under a
 * list view, so a pasted link has to be resolved into one of those locations at
 * open time; carrying only the post id keeps it working as the post changes
 * state. Readers who can't open it land here too, hence the failure states.
 */
export function PostPermalink(): JSX.Element {
  const { postId } = useParams<{ postId: string }>()
  const { account, loading: accountLoading } = useAccount()
  const currentOrganizationId = account?.currentOrganization?.id ?? null
  const { switchOrganization } = useOrganizationSwitcher()
  // Re-renders before the switch navigates away, so latch it.
  const hasStartedSwitchRef = useRef(false)
  const [switchFailed, setSwitchFailed] = useState(false)

  const { data, loading, error } = useQuery(GetPostPermalink, {
    variables: { id: postId ?? '' },
    skip: !postId,
  })

  const post = data?.post
  const target = post
    ? buildPostPermalinkTarget({
        id: post.id,
        status: post.status,
        channelId: post.channel.id,
        hasSiblingPosts: (post.postGroup?.posts.length ?? 0) > 1,
      })
    : null

  const canOpenPost = !!post && canPerform(post.channel, 'viewPublish')

  // `getUpdate` authorizes against the post's organization, not the current one,
  // so a cross-org link resolves while the shell stays on the wrong org.
  //
  // Switching writes the reader's current organization server-side, so it must
  // only happen on the path that actually opens the post. Effects run after
  // whichever branch below returned, so without this guard a reader who is
  // denied the post would still be moved to another organization and navigated
  // away from the denial they were shown.
  const organizationToSwitchTo =
    post &&
    canOpenPost &&
    currentOrganizationId &&
    post.channel.organizationId !== currentOrganizationId
      ? post.channel.organizationId
      : null

  useEffect(() => {
    if (!organizationToSwitchTo || !target || hasStartedSwitchRef.current) {
      return
    }
    hasStartedSwitchRef.current = true
    // Membership is proven — the org gate would have denied the query otherwise.
    switchOrganization(organizationToSwitchTo, { redirectTo: target }).catch(
      // Without this the switch stays pending forever and the reader keeps
      // watching a spinner. `switchOrganization` has already toasted.
      () => setSwitchFailed(true),
    )
  }, [organizationToSwitchTo, target, switchOrganization])

  // A pasted link is a cold load where the post can resolve first. Deciding then
  // reads the current org as unknown and skips the switch.
  if (loading || accountLoading) {
    return <CenteredLoader />
  }

  if (error || !post) {
    return <PostPermalinkFailureState failure={classifyPostFetchError(error)} />
  }

  if (!canOpenPost) {
    return (
      <AccessDenied
        channel={post.channel}
        description={
          <>
            You don&apos;t have publishing access to{' '}
            <Text weight="bold">{getChannelNameForDisplay(post.channel)}</Text>.
            Ask an owner or admin for access, then open the link again.
          </>
        }
      />
    )
  }

  if (switchFailed) {
    return <PostPermalinkFailureState failure="unexpected" />
  }

  // The effect navigates once the switch completes.
  if (organizationToSwitchTo || !target) {
    return <CenteredLoader />
  }

  return <Navigate to={target} replace />
}

const CenteredLoader = (): JSX.Element => (
  <div className={styles.centeredLoader}>
    <Loader />
  </div>
)

/** Exported so Storybook can reach each state without a router or Apollo mock. */
export const PostPermalinkFailureState = ({
  failure,
}: {
  failure: PostFetchFailure
}): JSX.Element => {
  if (failure === 'accessDenied') {
    return (
      <AccessDenied description="Ask an owner or admin for access, then open the link again." />
    )
  }

  if (failure === 'unavailable') {
    return (
      <EmptyState size="large" variant="neutral">
        <EmptyState.Icon>
          <Link2OffIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>
          This post isn&apos;t available anymore
        </EmptyState.Heading>
        <EmptyState.Description>
          It looks like this post was deleted. Check with whoever shared the
          link if you still need it.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  return (
    <EmptyState size="large" variant="critical">
      <EmptyState.Icon>
        <WarningIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Something went wrong</EmptyState.Heading>
      <EmptyState.Description>
        We couldn&apos;t open this post. Try the link again, or check with
        whoever shared it.
      </EmptyState.Description>
    </EmptyState>
  )
}
